/* Created by Interactive 63 - https://interactive63.com */
/**
 * The two dialogs for bringing in a shape to morph into: pasted path data, and a shape chosen
 * from another SVG file.
 */
import { sanitiseSvg } from '../../core/import/sanitise';
import { fitPathTo } from '../../core/morph/bounds';
import { geometryOf } from '../../core/morph/convert';
import { toCubicSubpaths } from '../../core/morph/normalise';
import { h } from '../ui/dom';
import { toast } from '../ui/toast';
import { shapeThumbnail } from './thumbnail';

const SHAPE_SELECTOR = 'path, rect, circle, ellipse, line, polyline, polygon';

/** Shows a dialog and resolves with the chosen path data, or null if it was cancelled. */
function showDialog(
  title: string,
  build: (done: (d: string | null) => void) => HTMLElement[],
): Promise<string | null> {
  return new Promise((resolve) => {
    const dialog = h('dialog', { class: 'settings-dialog shape-dialog', 'aria-label': title });
    let result: string | null = null;
    const done = (d: string | null): void => {
      result = d;
      dialog.close();
    };
    dialog.addEventListener('close', () => {
      dialog.remove();
      resolve(result);
    });
    dialog.append(
      h('header', { class: 'dialog-header' }, h('h3', null, title)),
      h('div', { class: 'scene-settings' }, ...build(done)),
    );
    document.body.append(dialog);
    dialog.showModal();
  });
}

/** Asks for path data. The text is checked as it is typed and previewed. */
export function pastePathDialog(): Promise<string | null> {
  return showDialog('Paste path data', (done) => {
    const text = h('textarea', {
      class: 'path-text',
      rows: 6,
      'aria-label': 'Path data',
      placeholder: 'M10 10 L90 10 L50 90 Z',
      spellcheck: 'false',
    });
    const message = h('p', { class: 'settings-note' }, 'Paste the d attribute of a path.');
    const preview = h('div', { class: 'shape-preview' });
    const use = h('button', { type: 'button', class: 'primary', disabled: true }, 'Use this shape');
    const cancel = h('button', { type: 'button' }, 'Cancel');
    text.addEventListener('input', () => {
      const d = text.value.trim();
      const result = d === '' ? null : toCubicSubpaths(d);
      use.disabled = !result?.ok;
      message.textContent =
        d === ''
          ? 'Paste the d attribute of a path.'
          : result?.ok
            ? 'Path data looks good.'
            : (result?.message ?? '');
      preview.replaceChildren(result?.ok ? shapeThumbnail(d, 96) : '');
    });
    use.addEventListener('click', () => {
      done(text.value.trim());
    });
    cancel.addEventListener('click', () => {
      done(null);
    });
    return [text, message, preview, h('div', { class: 'dialog-actions' }, cancel, use)];
  });
}

/** Lets someone choose an SVG file, then one of its shapes. */
export function fromFileDialog(currentD: string): Promise<string | null> {
  return showDialog('Shape from another SVG', (done) => {
    const input = h('input', {
      type: 'file',
      accept: '.svg,image/svg+xml',
      'aria-label': 'SVG file',
    });
    const fit = h('input', { type: 'checkbox', checked: true });
    const grid = h('div', { class: 'shape-grid' });
    const message = h(
      'p',
      { class: 'settings-note' },
      'Choose an SVG file to pick a shape from it.',
    );
    const cancel = h('button', { type: 'button' }, 'Cancel');
    cancel.addEventListener('click', () => {
      done(null);
    });
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return;
      void file.text().then((text) => {
        // Uploaded SVG is untrusted, so it goes through the same sanitiser as an import.
        const clean = sanitiseSvg(text);
        const doc = clean === '' ? null : new DOMParser().parseFromString(clean, 'image/svg+xml');
        const shapes = doc
          ? Array.from(doc.querySelectorAll(SHAPE_SELECTOR))
              .map((el) => ({ el, d: geometryOf(el) }))
              .filter((s) => s.d !== '')
          : [];
        if (shapes.length === 0) {
          message.textContent = 'No paths or shapes were found in that file.';
          grid.replaceChildren();
          return;
        }
        message.textContent = 'Choose a shape.';
        grid.replaceChildren(
          ...shapes.slice(0, 60).map(({ el, d }) => {
            const button = h(
              'button',
              {
                type: 'button',
                class: 'shape-choice',
                title: el.getAttribute('id') ?? el.localName,
              },
              shapeThumbnail(d, 64),
            );
            button.addEventListener('click', () => {
              if (fit.checked && currentD !== '') done(fitPathTo(d, currentD));
              else done(d);
            });
            return button;
          }),
        );
        if (shapes.length > 60) toast.info('Showing the first 60 shapes in that file.');
      });
    });
    return [
      input,
      h('label', { class: 'settings-check' }, fit, ' Fit to current shape'),
      message,
      grid,
      h('div', { class: 'dialog-actions' }, cancel),
    ];
  });
}
